/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-07 14:05:30
 *
 *******************************************************/
// @flow
//
import _ from 'lodash';

import React, { Component } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Modal, Icon, TextStyle, Button, TextField, Select, Link, Badge } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
// import { Link } from 'react-router-dom';
import { MobileCancelMajor, ExternalMinor } from '@shopify/polaris-icons';
import {
  formatPriceDisplay,
  buildAnalyticsInstance,
  getDiscountCodeValue
} from 'src/helpers/subscription-v3-helper';
import { DATE_FORMAT } from 'src/constants/language-setting';
import {
  REVIEW_ADDON_CONTENT,
  REVIEW_ADDON_CONTENT_V2,
  ANALYTICS_ADDON,
  DISCOUNT_ERRORS
} from 'src/constants/subscription';
import { buildRedirectUrl, getShop } from 'src/helpers';

import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { allowToShowBFCM23, BFCM23_CODE } from 'src/helpers/dashboard-helper';
import { routes } from 'src/routes';
import NoteIcon from 'src/assets/images/subscription-v3/addon/note.svg';
import DownloadIcon from 'src/assets/images/subscription-v3/addon/download.svg';
import StarIcon from 'src/assets/images/subscription-v3/addon/star.svg';
import { Carousel, CarouselItem } from 'src/components/views/common/carousel';

type State = {
  showDiscountCodeInput: boolean,
  invoice: Object,
  contentComponent: Object,

  // Store analytic addon
  analyticList: Array<any>,
  analytic: string,
  currentAnalyticPrice: number,
  code: string,
  showVerifyCodeNotice: boolean // Show verify discount code result
};

function checkEnableUpsell(tenantApp, allAddon = {}, currentStatus) {
  const addOnTrials = _.get(allAddon, 'addOnTrials', []);
  const { selectedPlan = '' } = tenantApp;

  if (!selectedPlan || selectedPlan === '') return false;

  const findAddonDoneTrial = addOnTrials.find(addon => {
    return addon.remainingDays === 0 || moment(addon.trialEndDate * 1000).isBefore(moment());
  });

  if (findAddonDoneTrial && currentStatus === 'subscribeNow') return true;
  return false;
}

function getProductPackageCompareByProductTenant(tenantApp, packages = []) {
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  const planMatch = packages.find(pkg => {
    return pkg.products > currentTotalProducts;
  });
  return _.get(planMatch, 'products', 1000);
}

class ReviewModal extends Component<WithI18nProps, State> {
  state = {
    showDiscountCodeInput: false,
    invoice: {},
    contentComponent: {},

    // Store analytic addon
    analyticList: [],
    analytic: null,
    currentAnalyticPrice: 0,
    code: '',
    showVerifyCodeNotice: false // Show verify discount code result
  };

  componentDidMount() {
    const { addon, getAddonStatus, analyticInput } = this.props;
    const { price, name, addonName } = addon;
    getAddonStatus(addonName);

    const { currentAnalyticPrice, analytic } = this.state;

    const contentComponent = REVIEW_ADDON_CONTENT.find(e => e.name === name);

    const analyticPrice = currentAnalyticPrice ? currentAnalyticPrice : ANALYTICS_ADDON[0].price;
    let invoice = {
      total: name === 'analytics' ? analyticPrice : price,
      originTotal: name === 'analytics' ? analyticPrice : price
    };

    this.setState({
      invoice,
      contentComponent,
      currentAnalyticPrice: analyticPrice
    });

    if (_.includes(addonName, 'analytics') || analyticInput) {
      this.setState({
        analytic: analyticInput ? analyticInput : analytic ? analytic : ANALYTICS_ADDON[0].value
      });
    }
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    const { discount, remainCycles, addon } = nextProps;
    const { dcRawValue: discountValue } = getDiscountCodeValue(discount);
    const discountType = _.get(discount, 'discountType', 'percentage');

    const { discount: discountPrev } = this.props;
    const { dcRawValue: discountValuePrev } = getDiscountCodeValue(discountPrev);

    // Init analytics list in case add in is analytics
    const { price, name } = addon;

    // Update total price and discount price when apply discount or add add-on
    if (discountValue !== discountValuePrev || this.state.analytic !== nextState.analytic) {
      const analyticPrice = ANALYTICS_ADDON.find(e => e.value === nextState.analytic)?.price || 0;

      const originTotal = name === 'analytics' ? analyticPrice : price;

      let total = originTotal;
      let discountAmount = 0;

      let expiredDate = '';
      if (discountValue) {
        discountAmount =
          discountType === 'percentage' ? originTotal * discountValue : discountValue;
        total = originTotal - discountAmount;
      }

      if (remainCycles) {
        const expiredAt = new Date().setDate(new Date().getDate() + remainCycles * 30);
        expiredDate = moment(expiredAt).format(DATE_FORMAT['en'].longDate);
      }

      this.setState({
        invoice: {
          discountValue,
          discountType,
          remainCycles,
          total,
          originTotal,
          discountAmount,
          expiredDate
        },
        currentAnalyticPrice: analyticPrice
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    const { confirmationUrl } = nextProps;

    // Redirect to shopify confirmation page
    if (confirmationUrl) {
      window.top.location.href = confirmationUrl;
    }

    // Show / hide verify discount code result
    const { verifying: prevVerifying, analyticInput: analyticInputPrev } = this.props;
    const { verifying: nextVerifying, analyticInput } = nextProps;

    if (prevVerifying === true && nextVerifying === false) {
      // Show verification discount code
      this.setState({ showVerifyCodeNotice: true });
    }
    const { addon, invoiceSubscribed, getInvoiceSuccess, gettingInvoice } = nextProps;

    if (getInvoiceSuccess === true && gettingInvoice === false && addon.name === 'analytics') {
      const analyticList = buildAnalyticsInstance(addon, invoiceSubscribed);

      // first value in list analytic available to subscribe
      const firstAnalytic = analyticList.length > 0 ? analyticList[0].value : '365';

      // Ensure data analytic already in list analytic
      const correctAnalytic =
        this.state.analytic && parseInt(this.state.analytic) > parseInt(firstAnalytic)
          ? this.state.analytic
          : firstAnalytic;

      this.setState({
        analyticList,
        analytic: correctAnalytic,
        currentAnalyticPrice: analyticList.find(e => e.value === correctAnalytic)?.price || 39
      });
    }

    // Get input from card
    if (analyticInputPrev !== analyticInput && analyticInput) {
      this.setState({ analytic: analyticInput });
    }
  }

  renderHighlightUpsell = (products, analytic) => {
    const { i18n, addon = {} } = this.props;
    const name = _.get(addon, 'name', '');

    const openComparePage = () => {
      const shop = getShop();
      const redirectUrl = buildRedirectUrl(shop, routes.subscription.comparison(products));
      window.open(redirectUrl, '_blank');
    };

    if (
      ['aiPowerSearch', 'aiPowerRecommendation', 'visualMerchandising'].includes(name) ||
      (name === 'analytics' && analytic === '90')
    ) {
      return (
        <div className="addon-modal-highlight">
          <ul>
            <li>
              <p>
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.highlightContent', {
                  t: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.upsellMsgEssPro')}
                    </TextStyle>
                  )
                })}
              </p>
              <span className="img-new-tab">
                <Link onClick={openComparePage}>
                  <span>{i18n.translate('AdminHTML.Common.Action.learnMore')}</span>
                  <Icon source={ExternalMinor} color="interactive" />
                </Link>
                <span style={{ color: 'var(--p-interactive)' }}>.</span>
              </span>
            </li>
          </ul>
        </div>
      );
    }

    if ((name === 'analytics' && analytic === '180') || name === 'variantDisplay') {
      return (
        <div className="addon-modal-highlight">
          <ul>
            <li>
              <p>
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.highlightContent', {
                  t: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.upsellMsgPro')}
                    </TextStyle>
                  )
                })}
              </p>
              <span className="img-new-tab">
                <Link onClick={openComparePage}>
                  {i18n.translate('AdminHTML.Common.Action.learnMore')}
                  <Icon source={ExternalMinor} color="interactive" />
                </Link>
                <span style={{ color: 'var(--p-interactive)' }}>.</span>
              </span>
            </li>
          </ul>
        </div>
      );
    }
    return null;
  };

  openHelpdoc = url => {
    window.open(url, '_blank');
  };

  render() {
    const {
      showDiscountCodeInput = false,
      invoice = {},
      analytic,
      code,
      showVerifyCodeNotice,
      analyticList = [],
      currentAnalyticPrice
    } = this.state;
    const {
      active,
      i18n,
      statusStore,
      verifying,
      addon = {},
      trialEndDate,
      hadSubscribed = false,
      addonSubscribing,
      invoiceSubscribed,
      msgVerifyError,
      tenantApp,
      packagePlans,
      allAddon
    } = this.props;
    const {
      discountValue = 0,
      remainCycles = 0,
      total = 0,
      originTotal = 0,
      discountAmount = 0,
      expiredDate = ''
    } = invoice;

    const { discountCodeApplied = {} } = invoiceSubscribed;
    const { name, allowTrial, status, downloads } = addon;
    // Get current status addon allowTrial || subscribe || added
    let currentStatus = status
      ? status
      : !['trial', 'expiredSoon'].includes(statusStore) && (hadSubscribed || !allowTrial)
      ? 'subscribeNow'
      : 'addToTrial';
    if (!['trial', 'expiredSoon'].includes(statusStore) && currentStatus === 'addToTrial') {
      currentStatus = 'startTrial';
    }

    const enableUpsell = checkEnableUpsell(tenantApp, allAddon, currentStatus);

    // Check if store still not apply discount code before
    const allowApplyDiscountCode =
      _.isEmpty(discountCodeApplied) && !['trial', 'expiredSoon'].includes(statusStore);

    // Option available of analytic
    const options = analyticList.map(e => ({
      value: e.value,
      label: i18n.translate(e.label).toLowerCase()
    }));

    // Calculation expired trial of add-on from today
    const trialExpiredAt = trialEndDate ? moment(new Date(trialEndDate * 1000)).format('LL') : '';
    const bfcm23 = allowToShowBFCM23(tenantApp);
    const data = REVIEW_ADDON_CONTENT_V2.find(item => item.name === addon.name);

    const renderCampaignBanners = data.slides.map((banner, index) => (
      <li className="carousel-item">
        <div className="slide-bg">
          <div className="title">{i18n.translate(banner.title)}</div>
          <div className="des">{i18n.translate(banner.des)}</div>
          <img className="img" src={banner.img} alt="img" width={260} height={200} />
          <div className="hover"></div>
        </div>
      </li>
    ));

    return (
      <Modal large open={active} onClose={this.handleCancel}>
        <div className="review-addon-modal">
          <div className="review-addon-v2">
            <div className="heading">
              <p className="title" style={{ textTransform: 'uppercase' }}>
                {i18n.translate(data.title)}
              </p>
              <div className="detail-wrap">
                <div className="detail" onClick={() => this.openHelpdoc(data.url)}>
                  <img src={NoteIcon} alt="note" />
                  <span>
                    {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddonV2.common.moreDetail')}
                  </span>
                </div>
              </div>
              {downloads && (
                <div className="download">
                  <img src={DownloadIcon} width={14} height={14} alt="download" />
                  <span>
                    {downloads + 100}{' '}
                    {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddonV2.common.downloads')}
                  </span>
                </div>
              )}
            </div>
            <div className="block">
              <div className="title">{i18n.translate(data.description)}</div>
              <div className={`list-addon-ui has-${data.blockItemsCol}-col`}>
                {data.blockItems.map((item, index) => {
                  return (
                    <div className="item" key={index}>
                      <div className="img">
                        <img src={StarIcon} alt="star" />
                      </div>
                      <span className="t1" style={{ fontWeight: '500' }}>
                        {i18n.translate(item.label)}
                        {item.isNew && (
                          <span style={{ marginLeft: '6px' }}>
                            <Badge status="info">
                              {i18n.translate('AdminHTML.Common.Badge.new')}
                            </Badge>
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="slide">
              <Carousel
                hasSlideControls={true}
                alwaysShowSlide={true}
                autoDisplay={true}
                hideAction={false}
                items={renderCampaignBanners}
                renderItem={({ item, isSnapPoint, index }) => (
                  <CarouselItem key={`element-${index}`} isSnapPoint={isSnapPoint}>
                    {item}
                  </CarouselItem>
                )}
              />
            </div>
          </div>
          <div className="addon-billing">
            <div className="billing-title">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.upgradeHeading')}
              </TextStyle>
              <Button onClick={this.handleCancel} plain icon={MobileCancelMajor} />
            </div>
            {bfcm23.allow && bfcm23.type === 'professional365' && (
              <div className="billing-title-code-bfcm23" style={{ marginBottom: '20px' }}>
                {i18n.translate(
                  'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Package_apply_analytic',
                  {
                    code: <b>{BFCM23_CODE.ANALYTIC365}</b>
                  }
                )}
              </div>
            )}
            {enableUpsell &&
              this.renderHighlightUpsell(
                getProductPackageCompareByProductTenant(tenantApp, packagePlans?.basic),
                analytic
              )}
            <div className="addon-info">
              <p>{i18n.translate(addon.title)}</p>
              <TextStyle variation="strong">
                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                  ${formatPriceDisplay(name === 'analytics' ? currentAnalyticPrice : addon.price)}
                </span>
              </TextStyle>
            </div>
            {name === 'analytics' && (
              <Select value={analytic} onChange={this.handleChangeAnalytic} options={options} />
            )}
            <p className="sub-addon-info">
              {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.subLabelBilling')}
            </p>

            {allowApplyDiscountCode && (
              <div className="discount-code-info">
                {showVerifyCodeNotice && code && discountValue ? (
                  <div className="discount-applied">
                    <p>
                      {i18n.translate(
                        'AdminHTML.SubscriptionV3.ReviewPackage.textFieldDiscount_Label'
                      )}
                    </p>
                    <div
                      className={`code ${
                        bfcm23.allow && bfcm23.type === 'professional365' && 'bfcm23'
                      }`}>
                      <div>
                        <p>{code}</p>
                        <Button plain icon={MobileCancelMajor} onClick={this.clearDiscountCode} />
                      </div>
                      <TextStyle variation="strong">
                        -${formatPriceDisplay(discountAmount)}
                      </TextStyle>
                    </div>
                    <div>
                      {remainCycles &&
                        remainCycles > 0 &&
                        i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.discountPreview', {
                          duration: remainCycles,
                          totalInvoice: <strong>{total}</strong>,
                          date: <strong>{expiredDate}</strong>,
                          originalPrice: <strong>{originTotal}</strong>
                        })}
                    </div>
                    <div>
                      <p>
                        {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.termAndCondition', {
                          t1: (
                            <span
                              onClick={this.handledTermNCondition}
                              style={{ color: '#2C6ECB', cursor: 'pointer' }}>
                              {i18n.translate(
                                'AdminHTML.SubscriptionV3.ReviewPackage.termAndCondition_t1'
                              )}
                            </span>
                          )
                        })}
                      </p>
                    </div>
                  </div>
                ) : showDiscountCodeInput ? (
                  <div>
                    <div className="discountCode">
                      <TextField
                        value={code}
                        label={i18n.translate(
                          'AdminHTML.SubscriptionV3.ReviewPackage.textFieldDiscount_Label'
                        )}
                        onChange={this.handleChangeCode}
                        placeholder={i18n.translate(
                          'AdminHTML.SubscriptionV3.Common.discountCodePlaceHolder'
                        )}
                        connectedRight={
                          <Button
                            primary
                            onClick={this.verify}
                            loading={verifying}
                            disabled={code ? false : true}>
                            {i18n.translate('AdminHTML.SubscriptionV3.Actions.apply')}
                          </Button>
                        }
                      />
                    </div>
                    {/* Code is not empty or discountValue > 0*/}
                    {showDiscountCodeInput && showVerifyCodeNotice && code && !discountValue && (
                      <div className={'applied error'}>
                        {DISCOUNT_ERRORS[msgVerifyError]
                          ? i18n.translate(DISCOUNT_ERRORS[msgVerifyError])
                          : msgVerifyError}
                      </div>
                    )}
                  </div>
                ) : (
                  <p onClick={() => this.toggleChangeState('showDiscountCodeInput')}>
                    {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.addDiscount')}
                  </p>
                )}
              </div>
            )}
            <div className="divider" />
            <div className="total">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.subTotal')}
              </TextStyle>
              <TextStyle variation="strong">
                <span style={{ fontWeight: '700' }}>${formatPriceDisplay(total)}</span>
              </TextStyle>
            </div>
            <Button loading={addonSubscribing} primary onClick={this.handleSubscribeAddon}>
              {i18n.translate(`AdminHTML.SubscriptionV3.Actions.${currentStatus}`)}
            </Button>
            <div className="describe-button">
              {currentStatus === 'addToTrial' || currentStatus === 'startTrial' ? (
                i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.describeAction01', {
                  date: <TextStyle variation="strong">{trialExpiredAt}</TextStyle>
                })
              ) : (
                <>
                  <p>{i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.describeAction02')}</p>
                  <p>{i18n.translate('AdminHTML.SubscriptionV3.ReviewAddon.describeAction03')}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  handleCancel = () => {
    this.props.handleCancel();
    this.setState({
      currentAnalyticPrice: null,
      analytic: null,
      analyticList: []
    });
    this.clearDiscountCode();
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  handledTermNCondition = () => {
    this.props.handledTermNCondition();
  };
  // Change discount code
  handleChangeCode = code => {
    if (code) {
      this.setState({ code, showVerifyCodeNotice: false });
    } else {
      // Hide discount code verification result
      this.setState({ code, showVerifyCodeNotice: false });

      this.props.clearDiscountCode();
    }
  };

  // Clear discount code
  clearDiscountCode = () => {
    const { clearDiscountCode } = this.props;
    clearDiscountCode();
    this.handleChangeCode('');
    this.setState({ showDiscountCodeInput: false });
  };
  // Verify discount code
  verify = () => {
    const { code, analytic } = this.state;
    const { addon, verifyDiscountCode, selectedPlan } = this.props;
    let addonName = addon.addonName;
    if (_.includes(addonName, 'analytics')) {
      addonName = `${addon.addonName}-${analytic}`;
    }
    verifyDiscountCode(code, 'add-on-page', [addonName], selectedPlan);
  };

  // Handle change analytic add on
  handleChangeAnalytic = value => {
    this.setState({ analytic: value });
  };

  // Handle subscribe
  handleSubscribeAddon = async () => {
    const { addon = {}, discount } = this.props;
    const { code, analytic } = this.state;
    const { dcRawValue } = getDiscountCodeValue(discount);
    let { addonName } = addon;
    if (_.includes(addonName, 'analytics')) {
      const analyticAddon = ANALYTICS_ADDON.find(e => e.value === analytic);
      addonName = _.get(analyticAddon, 'name', '');
    }
    await this.props.subscribeAddon(addonName, dcRawValue ? code : '').then(response => {
      const { type, error = false } = response;
      let status = 'success';
      if (type === 'SUBSCRIBE_ADDON_FAILURE' || error) {
        status = 'error';
      }
      this.clearDiscountCode();
      this.props.handleOpenStatus(status);
    });

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  verifyDiscountCode: (code, subscribeOn, addOns, newPlanName) =>
    dispatch(subscriptionV3Actions.verifyDiscountCode(code, subscribeOn, addOns, newPlanName)),
  clearDiscountCode: () => dispatch(subscriptionV3Actions.clearDiscountCode()),
  subscribeAddon: (addonName, code) =>
    dispatch(subscriptionV3Actions.subscribeAddon(addonName, code, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const invoiceSubscribed = _.get(state.subscriptionV3, 'invoice', {});
  const trialEndDate = _.get(state.subscriptionV3, 'addonStatus.trialInfo.trialEndDate', 0);
  const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
  const statusStore = _.get(state.oauth, 'subscription.status', '');
  const calExpiredDay = new Date().getTime() / 1000 + 14 * 24 * 60 * 60;
  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});

  return {
    invoiceSubscribed,
    tenantApp,
    trialEndDate: ['trial', 'expiredSoon'].includes(statusStore)
      ? trialExpiresAt
      : trialEndDate
      ? trialEndDate
      : calExpiredDay,
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    currentTotalProducts: _.get(tenantApp, 'appParams.currentTotalProducts'),
    verifying: _.get(state.subscriptionV3, 'verifying', false),
    remainCycles: _.get(state.subscriptionV3, 'remainCycles', 0),

    discount: _.get(state.subscriptionV3, 'discountAddon', {}),
    msgVerifyError: _.get(state.subscriptionV3, 'msgError', null),
    confirmationUrl: _.get(state.subscriptionV3, 'confirmationUrl', ''),
    addonSubscribing: _.get(state.subscriptionV3, 'addonSubscribing', false),
    addonSubscribeSuccess: _.get(state.subscriptionV3, 'addonSubscribeSuccess', false),
    gettingInvoice: _.get(state.subscriptionV3, 'gettingInvoice', false),
    getInvoiceSuccess: _.get(state.subscriptionV3, 'getInvoiceSuccess', false),
    error: _.get(state.subscriptionV3, 'error', null),
    subscription: _.get(state.oauth, 'subscription', {}),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    packagePlans,
    allAddon: _.get(state.subscriptionV3, 'addons', [])
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ReviewModal));
